<template>
  <div
    class="k-date-picker"
    :class="[showDateTime ? '' : 'hide-date-time', { 'ph-no-capture': posthogNoCapture }]">
    <v-date-picker
      v-model="selectedDate"
      :mode="type"
      :masks="masks"
      :min-date="minDate"
      :popover="popover"
      model-config="modelConfig"
      :timezone="timezone"
      :minute-increment="minuteIncrement"
      :rules="timeRules"
      color="teal"
      :is-dark="isDark"
      is24hr
      is-inline
      @popover-did-show="addNoCaptureClass">
      <template #default="{ inputValue, inputEvents, showPopover }">
        <label v-if="label" :for="`${inputId}-${uuid}`">{{ label }}<span v-if="required" class="form-required"> *</span></label>
        <div class="form-entry input date-input">
          <i :class="iconClass" class="input-icon"/>
          <i v-if="!readOnly" class="fas fa-times reset-icon" @click="resetDate" title="Reset date" aria-hidden="true" />
          <input class="form-control date-picker-input"
                :id="`${inputId}-${uuid}`"
                :placeholder="inputPlaceholder"
                :value="selectedDate ? inputValue : ''"
                v-on="inputEvents || {}"
                @focus="setInitialDate"
                @keydown.enter="openPopoverFromKeyboard(showPopover)"
                @keydown.down.prevent="openPopoverFromKeyboard(showPopover)"
                :disabled="readOnly"
          >
        </div>
      </template>
    </v-date-picker>
  </div>
</template>

<style>
.form-entry .k-date-picker .date-input i.input-icon {
  left: 8px;
  top: 50%;
  padding: 0;
  transform: translateY(-50%);
}

.mx-datepicker-popup,
.mx-calendar-time {
  border-radius: 10px;
  font-family: hk-grotesk-light, sans-serif;
  font-weight: bold;
  z-index: 9999;
}

.k-search-dropdown-menu.timezone-dropdown {
  margin: 15px 0;
}

.vc-dark .vc-nav-item.is-active,
.vc-dark .vc-nav-item:hover {
  background-color: teal !important;
}

.vc-dark .vc-title,
.vc-dark .vc-arrow.vc-prev,
.vc-dark .vc-arrow.vc-next {
  background-color: var(--kate-panel-alt) !important;
}

.vc-dark .vc-nav-title,
.vc-dark .vc-nav-arrow {
  background-color: var(--kate-panel-alpha) !important;
}

.vc-dark .vc-nav-title:hover,
.vc-dark .vc-nav-arrow:hover,
.vc-dark .vc-nav-item {
  background-color: var(--kate-background-body-alpha) !important;
}

.hide-date-time .vc-time-header {
  display: none !important;
}

.vc-time-select-minutes,
.vc-time-select-hours {
  border: 1px solid var(--kate-panel-alt);
}

.vc-time-select-minutes:hover,
.vc-time-select-hours:hover,
.vc-time-select-minutes:focus,
.vc-time-select-hours:focus {
  background-color: var(--kate-panel-alt) !important;
  border: 1px solid var(--kate-panel-alt);
}

.date-input .form-control.date-picker-input[disabled] {
  background-color: var(--input-background) !important;
}
</style>

<style scoped>
.date-input {
  position: relative;
}

.date-input .form-control {
  padding-left: 30px;
  padding-right: 20px;
  text-overflow: ellipsis;
}

</style>

<script>
import TimeMixin from '@mixins/time-mixins';
import useThemeStore from '@stores/theme';
import 'v-calendar/style.css';

export default {
  mixins: [TimeMixin],
  props: {
    modelValue: {
      type: [Date, String],
    },
    popover: {
      type: Object,
    },
    inputPlaceholder: {
      type: String,
      required: false,
      default: 'Select date...',
    },
    minDate: {
      type: Date,
      required: false,
      default: undefined,
    },
    timeRules: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    mask: {
      type: String,
      required: false,
      default: 'DD-MM-YYYY',
    },
    modelConfig: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    type: {
      type: String,
      required: false,
      default: 'date',
    },
    minuteIncrement: {
      type: Number,
      required: false,
      default: 5,
    },
    timezone: {
      type: String,
      default: 'Europe/London',
    },
    inputId: {
      type: String,
      default: 'date-picker-input',
    },
    label: {
      type: String,
      default: '',
    },
    required: {
      type: Boolean,
      default: false,
    },
    showDateTime: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    posthogNoCapture: {
      type: Boolean,
      default: false,
    },
  },

  beforeMount() {
    this.uuid = this.$.uid;
  },

  data() {
    return {
      themeStore: useThemeStore(),
      selectedDate: this.sanitise(this.modelValue),
      uuid: null,
    };
  },

  watch: {
    modelValue() {
      this.selectedDate = this.modelValue;
    },
    selectedDate() {
      this.$emit('update:modelValue', this.selectedDate);
    },
  },

  computed: {
    masks() {
      return {
        input: this.mask,
      };
    },
    isDark() {
      if (this.themeStore.theme !== 'light') {
        return true;
      }
      return false;
    },
    iconClass() {
      return this.type === 'time' ? 'fas fa-clock' : 'fa-duotone fa-calendar';
    },
  },

  methods: {
    addNoCaptureClass() {
      this.$nextTick(() => {
        this.$el.querySelectorAll('.vc-base-select select').forEach(el => {
          el.classList.add('ph-no-capture');
        });
      });
    },
    setInitialDate() {
      if (!this.selectedDate && this.type === 'time') {
        this.selectedDate = new Date();
      }
    },
    openPopoverFromKeyboard(showPopover) {
      if (this.popover && !this.readOnly) {
        showPopover();
      }
    },
    resetDate() {
      this.selectedDate = undefined;
      this.$emit('reset');
    },
    sanitise(val) {
      if (!val) {
        return undefined;
      }
      if (val instanceof Date) {
        return val;
      }
      return this.getDate(val);
    },
  },
};
</script>
